import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_BASE from '../config/api';

const VENUE_TYPE_OPTIONS = [
  'Arena', 'Theater', 'Club', 'Festival Grounds',
  'Convention Center', 'Hotel', 'Outdoor', 'Other',
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia',
  'Germany', 'France', 'Spain', 'Italy', 'Japan', 'Brazil', 'Mexico', 'Other',
];

const SELECT_ARROW = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23565656' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")";

const INPUT_BASE = {
  width: '100%',
  backgroundColor: '#1a1a1a',
  borderRadius: '4px',
  padding: '10px 14px',
  color: '#F5F5F5',
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 400,
  fontSize: '15px',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease',
};

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && (
        <label
          style={{
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 500,
            fontSize: '13px',
            color: '#888888',
          }}
        >
          {label}
        </label>
      )}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, focused, onFocus, onBlur }) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{
        ...INPUT_BASE,
        border: `1px solid ${focused ? '#39FF14' : '#3a3a3a'}`,
      }}
    />
  );
}

function SelectInput({ value, onChange, options, placeholder, focused, onFocus, onBlur }) {
  return (
    <select
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{
        ...INPUT_BASE,
        border: `1px solid ${focused ? '#39FF14' : '#3a3a3a'}`,
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        color: value ? '#F5F5F5' : '#888888',
        backgroundImage: SELECT_ARROW,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        paddingRight: '32px',
      }}
    >
      <option value="" style={{ backgroundColor: '#1a1a1a', color: '#888888' }}>{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt} style={{ backgroundColor: '#1a1a1a', color: '#F5F5F5' }}>{opt}</option>
      ))}
    </select>
  );
}

export default function AddVenueModal({ isOpen, onClose, onSuccess }) {
  const { token } = useAuth();
  const [name, setName] = useState('');
  const [venueType, setVenueType] = useState('');
  const [website, setWebsite] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('United States');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [focused, setFocused] = useState({});

  const setFocus = (f, v) => setFocused((p) => ({ ...p, [f]: v }));

  if (!isOpen) return null;

  function resetForm() {
    setName(''); setVenueType(''); setWebsite('');
    setCity(''); setState(''); setCountry('United States');
    setError(null); setIsLoading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Venue name is required.'); return; }
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_BASE}/api/venues`,
        { name: name.trim(), venue_type: venueType, website, city, state, country },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess(res.data);
      onClose();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add venue.');
      setIsLoading(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '10px',
          width: '520px',
          maxWidth: '90vw',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 60px rgba(57,255,20,0.08)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#222222',
            padding: '20px 24px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid #3a3a3a',
          }}
        >
          <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '20px', color: '#FFFFFF' }}>
            Add Venue
          </span>
          <CloseBtn onClick={onClose} />
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Row 1 — Name */}
            <Field label="Venue Name *">
              <TextInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Venue name"
                focused={focused.name}
                onFocus={() => setFocus('name', true)}
                onBlur={() => setFocus('name', false)}
              />
            </Field>

            {/* Row 2 — Type + Website */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="Venue Type">
                <SelectInput
                  value={venueType}
                  onChange={(e) => setVenueType(e.target.value)}
                  options={VENUE_TYPE_OPTIONS}
                  placeholder="— Type —"
                  focused={focused.venueType}
                  onFocus={() => setFocus('venueType', true)}
                  onBlur={() => setFocus('venueType', false)}
                />
              </Field>
              <Field label="Website">
                <TextInput
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://"
                  focused={focused.website}
                  onFocus={() => setFocus('website', true)}
                  onBlur={() => setFocus('website', false)}
                />
              </Field>
            </div>

            {/* Row 3 — City + State */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="City">
                <TextInput
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  focused={focused.city}
                  onFocus={() => setFocus('city', true)}
                  onBlur={() => setFocus('city', false)}
                />
              </Field>
              <Field label="State">
                <SelectInput
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  options={US_STATES}
                  placeholder="— State —"
                  focused={focused.state}
                  onFocus={() => setFocus('state', true)}
                  onBlur={() => setFocus('state', false)}
                />
              </Field>
            </div>

            {/* Row 4 — Country */}
            <Field label="Country">
              <SelectInput
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                options={COUNTRIES}
                placeholder="— Country —"
                focused={focused.country}
                onFocus={() => setFocus('country', true)}
                onBlur={() => setFocus('country', false)}
              />
            </Field>

            {error && (
              <div style={{
                color: '#BF2634', fontFamily: "'Roboto', sans-serif",
                fontWeight: 500, fontStyle: 'italic', fontSize: '14px',
                backgroundColor: 'rgba(191,38,52,0.1)', borderLeft: '3px solid #BF2634',
                padding: '10px 14px', borderRadius: '4px',
              }}>
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '16px 24px', borderTop: '1px solid #3a3a3a',
            display: 'flex', justifyContent: 'flex-end', gap: '12px',
          }}>
            <CancelBtn onClick={onClose} />
            <SubmitBtn isLoading={isLoading} />
          </div>
        </form>
      </div>
    </div>
  );
}

function CloseBtn({ onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: 'none', border: 'none',
        color: h ? '#F5F5F5' : '#565656',
        fontSize: '20px', cursor: 'pointer', lineHeight: 1,
        padding: '0 2px', transition: 'color 0.15s ease',
      }}
    >
      ✕
    </button>
  );
}

function CancelBtn({ onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: 'transparent',
        border: `1px solid ${h ? '#F5F5F5' : '#565656'}`,
        color: '#F5F5F5',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '14px',
        padding: '10px 20px',
        borderRadius: '4px',
        cursor: 'pointer',
        transition: 'border-color 0.15s ease',
      }}
    >
      Cancel
    </button>
  );
}

function SubmitBtn({ isLoading }) {
  const [h, setH] = useState(false);
  return (
    <button
      type="submit"
      disabled={isLoading}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        backgroundColor: h && !isLoading ? '#8B1C26' : '#BF2634',
        color: '#FFFFFF',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '14px',
        padding: '10px 20px',
        borderRadius: '4px',
        border: 'none',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.15s ease',
      }}
    >
      {isLoading ? 'Adding...' : 'Add Venue'}
    </button>
  );
}
